import { useCallback, useState } from 'react';

/**
 * One dialog per page, opened by spec.
 *
 * A page calls `ask({ ...spec, onConfirm })` where it used to call `confirm()`
 * or `prompt()`, and renders a single `<Modal {...modalProps} />`. The spec
 * carries the copy, so the wording of a dangerous action lives next to the call
 * that performs it rather than in a component prop soup.
 *
 * `busy` is held here too: the confirm button has to stay disabled while the
 * request it triggered is in flight, or a double click sends it twice — which on
 * this admin means two retries, and possibly two bookings.
 */
export function useModal() {
  const [spec, setSpec] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = useCallback(() => {
    setSpec(null);
    setBusy(false);
  }, []);

  const ask = useCallback(next => setSpec(next), []);

  const onConfirm = useCallback(async (value, note) => {
    if (!spec?.onConfirm) {
      close();
      return;
    }

    setBusy(true);
    try {
      // The dialog closes only once the action resolves, so the spinner and the
      // disabled button are visible for as long as the work takes.
      await spec.onConfirm(value, note);
      close();
    } catch (error) {
      // The page reports its own failures; the dialog just stops blocking.
      close();
    }
  }, [spec, close]);

  return {
    ask,
    close,
    modalProps: {
      // Spec first, then the wired handlers: spreading it last would let the
      // spec's own `onConfirm` shadow the wrapper that manages `busy`.
      ...(spec || {}),
      open: !!spec,
      busy,
      onConfirm,
      onClose: close
    }
  };
}
